import { createContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/authApi';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationAvailable, setRegistrationAvailable] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  }, []);

  const applyTokens = useCallback((accessToken, refreshToken, userData) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(userData);
  }, []);

  const login = useCallback(
    async (loginValue, password) => {
      const response = await authApi.login(loginValue, password);
      const { accessToken, refreshToken, user: userData } = response.data;
      applyTokens(accessToken, refreshToken, userData);
      return userData;
    },
    [applyTokens]
  );

  const register = useCallback(
    async (data) => {
      const response = await authApi.register(data);
      const { accessToken, refreshToken, user: userData } = response.data;
      applyTokens(accessToken, refreshToken, userData);
      setRegistrationAvailable(false);
      return userData;
    },
    [applyTokens]
  );

  const fetchSetup = useCallback(async () => {
    try {
      const response = await authApi.checkSetup();
      setRegistrationAvailable(response.data.registrationAvailable);
    } catch {
      setRegistrationAvailable(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await authApi.getMe();
      setUser(response.data.user);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    const init = async () => {
      await fetchSetup();
      await fetchUser();
    };
    init();
  }, [fetchSetup, fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        registrationAvailable,
        login,
        register,
        logout,
        refreshUser: fetchUser,
        refreshSetup: fetchSetup,
        isAdmin: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
